import type { ReactNode } from 'react';

import styles from './Alert.module.css';
import { CloseIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from './icons';

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  readonly severity?: AlertSeverity;
  readonly title?: ReactNode;
  readonly children: ReactNode;
  readonly onDismiss?: () => void;
  readonly dismissLabel?: string;
}

/**
 * Severity is carried by three independent channels — icon shape, an Arabic
 * text prefix, and colour — because design addon §4.2 forbids colour as the
 * only signal. Removing the icon or the prefix breaks that.
 */
const SEVERITY = {
  info: { Icon: InfoIcon, label: 'معلومة', role: 'status' as const },
  success: { Icon: SuccessIcon, label: 'تم بنجاح', role: 'status' as const },
  warning: { Icon: WarningIcon, label: 'تنبيه', role: 'status' as const },
  // Errors interrupt: assertive so a screen reader does not finish the
  // current sentence before announcing a failure.
  error: { Icon: ErrorIcon, label: 'خطأ', role: 'alert' as const },
} as const;

/** Inline notice — design addon §8.3. */
export function Alert({
  severity = 'info',
  title,
  children,
  onDismiss,
  dismissLabel = 'إخفاء التنبيه',
}: AlertProps) {
  const { Icon, label, role } = SEVERITY[severity];

  return (
    <div className={`${styles.alert} ${styles[severity]}`} role={role}>
      <span className={styles.icon}>
        <Icon />
      </span>

      <div className={styles.content}>
        {/* The severity word is always present for assistive tech; it is only
            visually hidden when a title already states the situation. */}
        {title ? (
          <p className={styles.title}>
            <span className="visually-hidden">{label}: </span>
            {title}
          </p>
        ) : (
          <p className={styles.title}>{label}</p>
        )}
        <div className={styles.message}>{children}</div>
      </div>

      {onDismiss ? (
        <button type="button" className={styles.dismiss} onClick={onDismiss}>
          <CloseIcon title={dismissLabel} />
        </button>
      ) : null}
    </div>
  );
}
